import React, { FunctionComponent, useEffect, useState, useRef } from 'react';
import {
	EpiAccount,
	FinancialPlanningSoftware,
} from '../../../../logic/domains/epi.domain';
import TermsAndConditions from '../TermsAndConditions/TermsAndConditions';
import ProductsForm from './ProductsForm';
import { deprecatedPredicate, toFPSMapper, toFpsIdentifier } from '../utils';
import {
	updateFps,
	UpdateAccountRes,
} from '../../../../logic/functions/epiAccounts.function';
import ActionButtons from '../Common/ActionButtons';
import { Form, Text, ScreenReaderText } from '@mlc/web-ui-toolkit';
import {
	addFormEvent,
	EVENT_FORM_COMPLETED,
	EVENT_FORM_ABANDONED,
} from '../../../../logic/services/digital-data.service';
import { showError } from '../../../../events/NotificationEmitter';
import { SomethingWentWrong } from '../../Common/Text/SomethingWentWrong';
import ToastContainer from '../../../containers/Notifications/ToastContainer';

type Props = {
	token: string;
	fpsOptions: FinancialPlanningSoftware[];
	account: EpiAccount;
	onModeChange?: () => void;
	onUpdate: (fps: FinancialPlanningSoftware[]) => void;
};

const UpdateProductOptions: FunctionComponent<Props> = ({
	token,
	fpsOptions,
	account,
	onModeChange,
	onUpdate,
}) => {
	const FORM_ID = 'epiSoftwareForm';
	const [updating, setUpdating] = useState(false);
	const [fpsSelected, setFpsSelected] = useState<string[] | undefined>(
		undefined
	);
	const [tcAccepted, setTCAccepted] = useState(false);
	const [fpsValid, setFpsValid] = useState(true);
	const [tcValid, setTCValid] = useState(true);
	const [ariaMessage, setAriaMessage] = useState('');
	const [showNotification, setShowNotification] = useState(false);

	const actionButtonRef = useRef<any>();

	useEffect(() => {
		setFpsSelected(
			account.fps.filter(deprecatedPredicate).map(toFpsIdentifier)
		);
	}, []);

	const complete = () => {
		addFormEvent(EVENT_FORM_COMPLETED, FORM_ID);
		switchMode();
	};

	const onCancelled = () => {
		addFormEvent(EVENT_FORM_ABANDONED, FORM_ID);
		switchMode();
	};

	const switchMode = () => {
		onModeChange && onModeChange();
	};

	const onUpdateHandler = async () => {
		if (validate()) {
			setAriaMessage('Processing please wait');
			setUpdating(true);
			const selected = fpsSelected!.map(toFPSMapper(fpsOptions));
			const result = await updateFps(token, selected);
			setUpdating(false);
			if (result === UpdateAccountRes.SUCCESS) {
				onUpdate(selected);
				complete();
			} else {
				showError(<SomethingWentWrong />, focus);
				focus();
			}
		}
	};

	const validate = () => {
		const fpsValid = fpsSelected !== undefined && fpsSelected.length > 0;
		setFpsValid(fpsValid);
		setTCValid(tcAccepted);
		return fpsValid && tcAccepted;
	};

	const focus = () => {
		if (actionButtonRef.current) {
			actionButtonRef.current.focus();
		}
	};

	return (
		<Form id={FORM_ID} onSubmit={(e: any) => e.preventDefault()}>
			<ToastContainer />
			<ScreenReaderText
				id="updateProductsForm"
				role="region"
				aria-live="assertive"
			>
				{ariaMessage}
			</ScreenReaderText>
			<ProductsForm
				isInvalid={!fpsValid}
				onCheckedHandler={setFpsSelected}
				fpsOptions={fpsOptions}
				value={fpsSelected}
			/>
			<TermsAndConditions
				isAccepted={setTCAccepted}
				name={'edit'}
				isInvalid={!tcValid}
			/>
			<ActionButtons
				onCancelClick={onCancelled}
				onUpdateClick={onUpdateHandler}
				isUpdating={updating}
				ariaLiveId="updateProductsForm"
				forwardedRef={actionButtonRef}
			/>
		</Form>
	);
};

export default UpdateProductOptions;
